import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import TagAtom from "../Tag";
import breakpoints from "../../../styles/breakpoints";
import { colors, fonts } from "../../../styles/variables";
import {
  trackingEvents,
  trackingEventClickedTagOnProductCardOptions,
  useTrackEvent,
} from "../../../helpers/Analytics";
import { GetWine_wine_wineProductionMethods } from "../../../types/__generated__/GetWine";

const TagsContainer = styled.div`
  flex-wrap: wrap;
`;

const Tag = styled(TagAtom)<{ color: string }>`
  display: inline-block;
  background-color: ${(props) => props.color};
  margin: 0 10px 10px 0;
  font-size: 11px;
  padding: 2px 10px;
  font-weight: 600;
  border-radius: 10px;
  ${breakpoints.iphone5} {
    font-size: 10px;
  }
`;

const DetailsTrayOrPageTag = styled(TagAtom)<{ color: string }>`
  display: inline-block;
  background-color: ${(props) => props.color};
  margin: 8px 8px 0 0;
  font-size: 12px;
  padding: 8px 16px;
  font-family: ${fonts.fontBauRegular};
  border-radius: 99px;
  border: 1px solid ${colors.black};
  ${breakpoints.iphone5} {
    font-size: 12px;
  }
  &:hover {
    background-color: ${colors.white};
  }
`;

interface ProductDetailsTagsProps {
  tags: {
    wineClassName?: string | undefined;
    wineTypeName?: string | undefined;
    wineRegionName?: string | undefined;
    wineRegionState?: string | null | undefined;
    wineCountryName?: string | undefined;
    wineProductionMethods?: GetWine_wine_wineProductionMethods[] | undefined;
    wineMakeName?: string | undefined;
    productTypeName?: string | undefined;
    productionCountryName?: string | undefined;
  };
  color?: string;
  handleToggleBottleInfoTray?: () => void;
  isInDetailsTrayOrPage?: boolean;
  pathname: string;
}

const ProductDetailsTags = ({
  color = colors.pinkSalmon,
  tags,
  handleToggleBottleInfoTray,
  isInDetailsTrayOrPage = false,
  pathname,
}: ProductDetailsTagsProps) => {
  const trackEvent = useTrackEvent();

  // Prevents duplication of tags with same name and class (Eg. Rose).
  const displayWineClassName = tags.wineClassName && tags.wineClassName !== tags.wineTypeName;
  const filteredTags = { ...tags };
  if (!displayWineClassName) delete filteredTags.wineClassName;

  /**
   * Toggles the bottle info tray and tracks the tag click.
   * */
  const handleOnTagClick = (tagName: string, tagValue: string) => {
    const eventToTrack = trackingEvents.TRACKING_EVENT_CLICKED_TAG_ON_PRODUCT_CARD;
    const options = {
      [trackingEventClickedTagOnProductCardOptions.TAG_NAME]: tagName,
      [trackingEventClickedTagOnProductCardOptions.TAG_VALUE]: tagValue,
      [trackingEventClickedTagOnProductCardOptions.IS_FROM_DETAILS]: isInDetailsTrayOrPage,
    };
    trackEvent(eventToTrack, options);
    handleToggleBottleInfoTray?.();
  };

  /**
   * Renders each tag.
   * */
  const renderTag = (tagName: string, tagValue: string) =>
    tagValue ? (
      <Link
        key={`${tagValue}`}
        to={{
          pathname,
          search: `?search=${encodeURIComponent(tagValue)}`,
        }}
        onClick={() => handleOnTagClick(tagName, tagValue)}
      >
        {isInDetailsTrayOrPage ? (
          <DetailsTrayOrPageTag key={`${tagValue}`} color={color}>
            {tagValue}
          </DetailsTrayOrPageTag>
        ) : (
          <Tag key={`${tagValue}`} color={color}>
            {tagValue}
          </Tag>
        )}
      </Link>
    ) : null;

  return (
    <TagsContainer>
      {Object.keys(filteredTags).map((tagName) => {
        // @ts-ignore
        const tagValue = filteredTags?.[tagName] ?? null;
        return Array.isArray(tagValue)
          ? tagValue.map((value) => renderTag(tagName, value?.name))
          : renderTag(tagName, filteredTags[tagName as keyof typeof filteredTags] as string);
      })}
    </TagsContainer>
  );
};

export default ProductDetailsTags;
